import { loadSpin, updateSpinInfoHotspots } from "./sagasActions";
import {
  setClickActionBoxContent,
  setClickActionBoxLocationFollowOffset,
  setClickActionBoxLocationType,
  setClickActionLink,
  setClickActionLocationFixedKeyword,
  setClickActionLocationFixedOffset,
  setClickActionType,
  setCurrentFrameIndex,
  updateAlgorithmInput,
  setHoverActionType,
  setCurrentStep,
  setTooltipContent,
  setTooltipStyle,
  setHotspotName,
  createHotspot,
  setFilenamesInLayer,
  setTemplate,
  setSpinInfo,
  resetHotspotEditor,
  setCommittedTheme,
  setClickActionBoxStyle,
  setPointerText,
  setBoundaries,
  setNotification,
  setNaturalDimensions,
  setCurrentlyOpenModal,
  setRequestedAlgorithm,
  setSpinURL,
} from "..";
import {
  addOneToKeys,
  convertObjectKeysToInteger,
  infoSettingsFrameIndexToLayerFrameObject,
  sanitizeHash,
  tryGetFromStorage,
} from "../../util/general";
import { clipToBounds } from "../../util/general";
import { getHotspotCount, getFrameCount } from "../selectors";
import { call, put, select, takeEvery } from "redux-saga/effects";
import {
  algorithms,
  dontShowTutorialStorageKeyName,
  hoverActions,
  modals,
  steps,
} from "../../util/constants";
import { getImageUrl } from "../../util/general";
import { setFolderURL } from "../folderURL/folderURL";
import { getIsEmbedded } from "../../util/params";
import { getThemeByName } from "../../themes";
import {
  setCurrentPointerVariant,
  setPointerColor0,
  setPointerSize,
} from "../pointerVariant/pointerVariant";
import {
  setBoxBackgroundColor,
  setBoxTextColor,
  setCurrentBoxVariant,
} from "../boxVariant/boxVariant";
import { cput } from "../../util/saga-util";
import { inferBounds, spinHotspotsToAlgorithmState } from "../../util/spin";
import { showSpinHint } from "../ui/ui";
import axios from "axios";
import { loadImagesInLayer } from "./loadImages";

function* reportError(error) {
  const defaultStep = getIsEmbedded() ? steps.loading : steps.init;
  console.log(error);
  yield put(
    setNotification({ message: `An error has occurred during loading` })
  );
  yield put(setCurrentStep(defaultStep));
}

function* loadImageData(layers, folderURL) {
  /*
    In `layers.[id].[id]` id's don't have an intrinsic meaning an only 
    serve the purpose of ordering the frames within a row (layer). 
    It does happen that the indexing is inconsistent between spins.
    For now layers beyond the one with the smallest id is ignored (assumed to be index 0)
    */
  function getObjectValueArraySortedByKey(obj) {
    const compareFunction = (a, b) => {
      const aKey = parseInt(a[0], 10);
      const bKey = parseInt(b[0], 10);
      if (aKey === bKey) {
        return 0;
      }
      return aKey > bKey ? 1 : -1;
    };
    return Object.entries(obj)
      .sort(compareFunction)
      .map((item) => item[1]);
  }
  const sortedLayers = getObjectValueArraySortedByKey(layers);
  const baseLayerFrames = sortedLayers[0];
  const sortedBaseLayerFilenames =
    getObjectValueArraySortedByKey(baseLayerFrames);
  yield put(setFilenamesInLayer({ filenames: sortedBaseLayerFilenames }));
  yield put(setCurrentFrameIndex({ frameIndex: 0 }));
  const firstImageFilename = sortedBaseLayerFilenames[0];
  const imgUrl = getImageUrl({ folderURL, filename: firstImageFilename });
  // get natural dimensions
  try {
    const imgInfo = yield call(axios.get, imgUrl + "?info");
    const { height: naturalHeight, width: naturalWidth } = imgInfo.data;
    yield put(setNaturalDimensions({ naturalHeight, naturalWidth }));
    yield put(setCurrentStep(steps.preview));
  } catch (error) {
    yield reportError(error);
    return;
  }
}

function* loadHotspotMessage({ hs, hotspotIndex }) {
  if ("box" in hs) {
    const box = hs.box;
    const content = box?.content;
    if (content && content.length > 0) {
      yield put(setClickActionBoxContent({ hotspotIndex, content }));
      yield put(setClickActionType({ hotspotIndex, actionType: "box" }));
    }
    const style = box?.style;
    if (style) {
      yield put(setClickActionBoxStyle({ hotspotIndex, style }));
    }
    const fixed = box?.fixed;
    const x = box?.x;
    const y = box?.y;
    if (x && y) {
      if (fixed) {
        yield put(
          setClickActionBoxLocationType({ hotspotIndex, value: "fixed" })
        );
        if (
          typeof x === "string" &&
          ["center", "left", "right"].indexOf(x.toLowerCase()) !== -1
        ) {
          yield put(
            setClickActionLocationFixedKeyword({
              hotspotIndex,
              coordinateName: "x",
              keywordValue: x.toLowerCase(),
            })
          );
        } else if (!isNaN(parseFloat(x))) {
          yield put(
            setClickActionLocationFixedOffset({
              hotspotIndex,
              coordinateName: "x",
              offsetValue: !isNaN(parseFloat(x)),
            })
          );
        } else {
          console.log(`Read an invalid box location x value: ${x}`);
        }
        if (
          typeof y === "string" &&
          ["center", "top", "bottom"].indexOf(y.toLowerCase()) !== -1
        ) {
          yield put(
            setClickActionLocationFixedKeyword({
              hotspotIndex,
              coordinateName: "y",
              keywordValue: y.toLowerCase(),
            })
          );
        } else if (!isNaN(parseFloat(y))) {
          yield put(
            setClickActionLocationFixedOffset({
              hotspotIndex,
              coordinateName: "y",
              offsetValue: !isNaN(parseFloat(y)),
            })
          );
        } else {
          console.log(`Read an invalid box location y value: ${y}`);
        }
      }
    } else {
      // box location is not fixed
      yield put(
        setClickActionBoxLocationFollowOffset({
          hotspotIndex,
          coordinateName: "x",
          coordinateValue: parseFloat(x),
        })
      );
      yield put(
        setClickActionBoxLocationFollowOffset({
          hotspotIndex,
          coordinateName: "y",
          coordinateValue: parseFloat(y),
        })
      );
    }
  }
  if ("link" in hs) {
    //TODO: check that this is a valid URL
    yield put(setClickActionType({ hotspotIndex, actionType: "link" }));
    yield put(setClickActionLink({ hotspotIndex, actionValue: hs.link }));
  }
  const tooltipContent = hs?.tooltip?.content;
  if (tooltipContent) {
    yield put(
      setHoverActionType({ hotspotIndex, actionType: hoverActions.tooltip })
    );
    yield put(
      setTooltipContent({
        hotspotIndex,
        value: tooltipContent,
      })
    );
  }
  const tooltipStyle = hs?.tooltip?.style;
  if (tooltipStyle) {
    yield put(
      setTooltipStyle({
        hotspotIndex,
        value: tooltipStyle,
      })
    );
  }
}

function* loadHotspotLocations({ hs, hotspotIndex, allFrameIds }) {
  // == LOAD LOCATIONS ==
  // Define boundaries and direction by emulating the three-point process with 1st, intermediate and last frame
  // It's a dirty hack and relies on frame ids being ordered
  // If there are discontinuities, they will get ignored
  // allFrameIds is the array of id's that we have in the items of `layers` structure
  if (!hs?.frames) {
    return;
  }
  let framesOneBasedSequential = convertObjectKeysToInteger(hs?.frames); //frames here are 1-based sequential frame index, this is dictated by SMV
  if (Object.keys(framesOneBasedSequential).includes("0")) {
    framesOneBasedSequential = addOneToKeys(framesOneBasedSequential);
  }
  //remove invalid records
  for (let i of Object.keys(framesOneBasedSequential)) {
    if (!parseInt(i) || i < 1 || i > allFrameIds.length) {
      console.log(
        `IMPORT ERROR: input frame index value ${i} is out of range! Not loading`
      );
      delete framesOneBasedSequential[i];
      continue;
    }
    if (!("pointer" in framesOneBasedSequential[i])) {
      console.log(`IMPORT ERROR: no pointer in frame${i}! Not loading`);
      delete framesOneBasedSequential[i];
      continue;
    }
    const { x, y } = framesOneBasedSequential[i].pointer;
    if (isNaN(parseFloat(x)) || isNaN(parseFloat(y))) {
      console.log(
        `IMPORT ERROR: invalid pointer coordinates ${framesOneBasedSequential[i]?.pointer}! Not loading`
      );
      delete framesOneBasedSequential[i];
      continue;
    }
  }
  // utility closure
  const N = yield select(getFrameCount);
  const createPayload = (oneBasedFrameIndex) => {
    const { layer, frame } = infoSettingsFrameIndexToLayerFrameObject(
      oneBasedFrameIndex,
      N
    );
    const pointer = framesOneBasedSequential[oneBasedFrameIndex]?.pointer;
    return {
      hotspotIndex,
      layer,
      frame,
      mouseCoordinatesInPercent: {
        //parseFloat is a lazy way to strip '%' character from the end
        x: clipToBounds(parseFloat(pointer?.x), 0, 100),
        y: clipToBounds(parseFloat(pointer?.y), 0, 100),
      },
    };
  };

  for (let oneBasedFrameIndex of Object.keys(framesOneBasedSequential)) {
    yield put(updateAlgorithmInput(createPayload(oneBasedFrameIndex)));
  }
  const input = spinHotspotsToAlgorithmState(framesOneBasedSequential, N);
  const bounds = inferBounds({ input, frameCount: N });
  for (let layer of Object.keys(input)) {
    yield put(
      setBoundaries({ hotspotIndex, layer, boundaries: bounds[layer] })
    );
  }
}

export function* loadHotspots(info, options) {
  const { loadLocations = true, editorData } = options ?? {};
  const allFrameIds = Object.keys(info.layers[1]);
  // load hotspots if any
  const hotspots = info?.settings?.hotspots;
  const hotspotsEditorData = editorData?.hotspots;
  const hotspotCount = yield select(getHotspotCount);
  if (hotspots) {
    for (
      let hotspotIndex = hotspotCount; //hotspotIndex into redux state, which is not zero when importing into spin with exiting hotspots
      hotspotIndex < hotspots.length + hotspotCount;
      hotspotIndex++
    ) {
      yield put(createHotspot());
      const incomingHotspotIndex = hotspotIndex - hotspotCount;
      const hs = hotspots[incomingHotspotIndex];
      //== LOAD NAMES ==
      const name = hs?.name;
      try {
        if (name) {
          yield put(setHotspotName({ hotspotIndex, name }));
        }
      } catch (error) {
        console.log(`cannot set name, the error was ${error}`);
      }
      //== LOAD MESSAGES ==
      yield loadHotspotMessage({ hs, hotspotIndex });
      if (loadLocations) {
        //custom HSE data in editorData field contains user input and supersedes
        //inference of thereof from the spin.settings.hotspot locations, which
        //are the output
        yield loadHotspotLocations({ hs, hotspotIndex, allFrameIds });
        yield put(
          setRequestedAlgorithm({ algorithm: algorithms.manual, hotspotIndex })
        );
      }
      //== LOAD THE POINTER TEMPLATE ==
      const template = hs?.pointer?.template; // for spins
      try {
        if (template) {
          yield put(setTemplate({ hotspotIndex, template }));
        }
      } catch (error) {
        console.error(
          `cannot set the pointer template, the error was ${error}`
        );
      }
      // == LOAD EXTRA DATA ==
      if (hotspotsEditorData) {
        const text = hotspotsEditorData[incomingHotspotIndex]?.pointerText;
        if (text) {
          yield put(setPointerText({ hotspotIndex, text }));
        }
      }
    }
  }
}

function* loadVariantStyleData(action) {
  const { pointer = {}, box = {} } = action.payload ?? {};
  yield cput(setCurrentPointerVariant, pointer.variant);
  yield cput(setPointerColor0, pointer.color0);
  yield cput(setPointerSize, pointer.size);
  yield cput(setCurrentBoxVariant, box.variant);
  yield cput(setBoxTextColor, box.textColor);
  yield cput(setBoxBackgroundColor, box.backgroundColor);
}

export function* loadSpinWorker(action) {
  const defaultStep = getIsEmbedded() ? steps.loading : steps.init;
  yield put(resetHotspotEditor());
  yield put(setCurrentStep(steps.loading));
  const { spinURL, info, folderURL, editorData } = action.payload;
  yield put(setFolderURL(folderURL));
  try {
    yield loadImageData(info.layers, folderURL);
    // FIXME: this only loads current layer images, run throughout all layers!
    yield call(loadImagesInLayer);
  } catch (error) {
    console.error(error);
    yield put(setCurrentStep(defaultStep));
    return;
  }

  try {
    yield loadHotspots(info, { editorData });
  } catch (error) {
    console.log(`Couldn't load hotspots, the error was ${error}`);
    console.log(error);
    yield put(
      setNotification({
        message: `An error has occurred during loading of spin`,
      })
    );
    yield put(setCurrentStep(defaultStep));
  }
  yield put(setSpinInfo(info));
  yield put(updateSpinInfoHotspots());
  const themeName = editorData?.themes?.theme?.name;
  if (themeName) {
    const theme = getThemeByName(themeName);
    yield put(setCommittedTheme(theme));
  }
  yield loadVariantStyleData({ payload: editorData?.styles });
  yield put(setSpinURL(sanitizeHash(spinURL)));
  yield put(setCurrentStep(steps.preview));
  // == Everything below is ran after the first render ==
  if (!tryGetFromStorage(dontShowTutorialStorageKeyName)) {
    yield put(setCurrentlyOpenModal(modals.videoTutorial));
  }
  yield put(showSpinHint());
}

export function* loadSpinWatcher() {
  yield takeEvery(loadSpin().type, loadSpinWorker);
}
